/**
 * Ledger double-entry — types + bất biến thuần (TDD §6.5/§7, ADR-0002).
 * Logic post/reversal đầy đủ sẽ hiện thực ở story S3.1/S3.3 (test-first theo TEST_STRATEGY §3).
 * File này định nghĩa hình dạng + invariant kiểm được mà không cần DB.
 */

export type AccountKind =
  | "CASH"
  | "MEMBER_RECEIVABLE"
  | "MEMBER_PAYABLE"
  | "INCOME"
  | "EXPENSE"
  | "LOAN_RECEIVABLE"
  | "EXTERNAL"
  // US-E18.F5-0198 (@UC9288): TIỀN CỌC đang treo ở bên thứ ba (villa/nhà xe). Là TÀI SẢN (quyền đòi
  // lại), KHÔNG PHẢI chi phí ⇒ đặt cọc DEBIT vào đây (không chạm EXPENSE); hoàn ⇒ CREDIT trả về
  // nguồn; mất ⇒ CREDIT và chỉ PHẦN MẤT chuyển sang EXPENSE.
  | "DEPOSIT_RECEIVABLE"
  // US-E18.F5-0198 (@UC9291): TÀI SẢN CHUNG đã mua (lều/loa) — nguyên giá CHƯA phân bổ vào chi phí.
  // Mua = DEBIT PREPAID_ASSET (không phải EXPENSE ngay); mỗi lần dùng = CREDIT PREPAID_ASSET /
  // DEBIT EXPENSE (khấu hao) ⇒ Σ khấu hao ≤ nguyên giá.
  | "PREPAID_ASSET"
  // 🔴🔴 US-E20.F2-0208 (@UC9327–@UC9330) — SỔ HIẾU HỈ HAI CHIỀU. **NGHĨA VỤ XÃ HỘI, KHÔNG PHẢI NỢ**:
  // "A mừng cưới B 500k ⇒ B nên đáp lễ khi A có việc" là ĐẠO LÝ, không phải khoản hệ thống đi đòi.
  //   RECIPROCAL_RECEIVABLE : mình ĐÃ đi lễ — giữ "quyền được đáp lễ"
  //   RECIPROCAL_PAYABLE    : mình ĐÃ nhận lễ — mang "nghĩa vụ đáp lễ"
  // 🔴 TÁCH BẠCH TUYỆT ĐỐI với MEMBER_RECEIVABLE/MEMBER_PAYABLE (nợ THẬT của quỹ): 2 kind này KHÔNG
  // vào `FinancialPositionService.hasNonZeroPosition`, KHÔNG sinh member_dues/debt_entries ⇒ người
  // còn "nợ đáp lễ" VẪN rời nhóm / xoá tài khoản BÌNH THƯỜNG (chặn oan mới là bug).
  // 🔴 KHÔNG bao giờ đi kèm chân CASH: cá nhân mừng cá nhân ⇒ quỹ KHÔNG mất/nhận đồng nào.
  | "RECIPROCAL_RECEIVABLE"
  | "RECIPROCAL_PAYABLE"
  // 🔴🔴 US-E20.F4-0210 (@UC9334–@UC9338) — TÀI SẢN ĐẦU TƯ BIẾN ĐỘNG GIÁ (crypto/CK/vàng). ADR 0006.
  // GIÁ VỐN (cost basis) của số ĐƠN VỊ đang nắm giữ — TÁCH TUYỆT ĐỐI KHỎI `CASH`:
  //   · mua : DEBIT ASSET_HOLDING / CREDIT CASH     (tiền rời két, đổi thành đơn vị tài sản)
  //   · bán : DEBIT CASH / CREDIT ASSET_HOLDING     (đúng GIÁ VỐN — hoàn vốn), lãi/lỗ ĐÃ THỰC HIỆN
  //           đi ở BÚT TOÁN RIÊNG (OTHER_INCOME / EXPENSE).
  // 🔴 GIÁ THỊ TRƯỜNG LÊN/XUỐNG ⇒ **0 BÚT TOÁN**: số dư ASSET_HOLDING LUÔN là GIÁ VỐN, KHÔNG BAO GIỜ
  // được "định giá lại" bằng bút toán. Lãi/lỗ CHƯA thực hiện là VIEW (@goppy/domain `markToMarket`)
  // — ghi nó vào sổ là đẻ tiền không có thật, và phá luôn khả năng đối chiếu sổ ↔ tiền thật.
  | "ASSET_HOLDING";

/**
 * Nguồn sự thật DUY NHẤT của `txType` (US-INFRA.F2-0163 S01, @UC9178).
 *
 * DB có CHECK `ledger_transactions_txtype_check` với đúng danh sách này. Kiểu `TxType` được
 * SUY RA từ mảng ⇒ thêm giá trị mới mà quên migration sẽ làm đỏ test drift
 * (`ledger-txtype-migration-sync.test.ts`), thay vì lọt xuống DB rồi hỏng âm thầm.
 */
export const TX_TYPES = [
  "CONTRIBUTION",
  "EXPENSE",
  // US-E16.F5-0186 (@UC9248): 3 loại dưới đây TỪNG CHẾT (khai enum + CHECK DB + filter đọc nhưng 0
  // producer). GIỜ CÓ PRODUCER THẬT: `RecordIncomeService` (apps/api/src/modules/ledger/
  // record-income.service.ts — POST /v1/funds/:id/income) post DEBIT CASH / CREDIT INCOME.
  // Lưới chống tái diễn: apps/api/src/modules/ledger/txtype-producer-coverage.spec.ts (mọi txType
  // trừ REVERSAL phải có ≥1 post-site THẬT trong apps/api/src).
  "SPONSORSHIP_MEMBER",
  "SPONSORSHIP_EXTERNAL",
  "OTHER_INCOME",
  "LOAN_OUT",
  "LOAN_REPAYMENT",
  "SETTLEMENT",
  "PAY_ON_BEHALF",
  "OFFSET",
  "REVERSAL",
  // US-E15.F3-0178 (@UC9217): producer advance_credit — quỹ nhận tiền member ứng trước
  // (DEBIT CASH / CREDIT MEMBER_PAYABLE). Thêm additive vào CHECK migration mới (sync test đọc
  // migration là NGUỒN SỰ THẬT hiện hành). KHÔNG xoá txType cũ.
  "ADVANCE_CREDIT",
  // US-E15.F4-0179 (@UC9220): cascade + partial refund — NCC hoàn tiền chảy ngược cho người ĐÃ GÁNH
  // (peer-internal balanced: DEBIT MEMBER_PAYABLE(debtor) / CREDIT MEMBER_RECEIVABLE(creditor), mirror
  // ngược SETTLEMENT). Thêm additive vào CHECK migration mới (sync test đọc migration mới nhất).
  "REFUND",
  // US-E18.F1-0194 (@UC9271): CHUYỂN TIỀN LIÊN SỔ (inter-fund transfer) — 2 chân, mỗi chân là 1 tx
  // RIÊNG (LedgerTransaction.fundId là single-fund ⇒ không thể 1 tx bắc cầu 2 sổ), post ATOMIC cùng
  // 1 transaction, link qua `linkedTxId` (trỏ nhau):
  //   FUND_TRANSFER_OUT (sổ nguồn): DEBIT EXTERNAL / CREDIT CASH  ⇒ CASH −N
  //   FUND_TRANSFER_IN  (sổ đích):  DEBIT CASH     / CREDIT EXTERNAL ⇒ CASH +N
  // 🔴 Σ CASH toàn nhóm BẤT BIẾN (−N + N = 0): tiền chỉ DỜI sổ, KHÔNG sinh/mất.
  // Producer: apps/api/src/modules/ledger/inter-fund-transfer.service.ts.
  "FUND_TRANSFER_OUT",
  "FUND_TRANSFER_IN",
  // US-E18.F2-0195 (@UC9276): ĐỔI HÌNH THÁI QUỸ (value form) — CÙNG SỔ, 2 chân CÙNG kind=CASH nhưng
  // KHÁC `valueForm` (kênh nắm giữ): CREDIT CASH@<from> / DEBIT CASH@<to> (vd rút mặt từ bank).
  // 🔴 Σ CASH TOÀN SỔ BẤT BIẾN (−N + N = 0): tiền chỉ ĐỔI KÊNH — KHÔNG thu, KHÔNG chi, KHÔNG sinh/mất.
  // Sự kiện TRUNG TÍNH ⇒ read-surface (fund-dashboard/transactions) CỐ Ý loại trừ khỏi thu/chi.
  // Producer: apps/api/src/modules/ledger/value-form.service.ts.
  "VALUE_FORM_SWITCH",
  // US-E18.F5-0198 (@UC9288–@UC9291) — CỌC + KHẤU HAO. 🔴 Cọc/tài sản = LUÂN CHUYỂN/TÀI SẢN, KHÔNG
  // phải chi phí ⇒ cần txType RIÊNG để read-surface KHÔNG đếm chúng là thu/chi:
  //   DEPOSIT_PLACE   : DEBIT DEPOSIT_RECEIVABLE / CREDIT CASH|MEMBER_PAYABLE  (0 chi phí)
  //   DEPOSIT_REFUND  : DEBIT CASH|MEMBER_PAYABLE / CREDIT DEPOSIT_RECEIVABLE  (0 chi phí — hoàn đủ)
  //   DEPOSIT_FORFEIT : DEBIT EXPENSE(mất) + DEBIT CASH|MEMBER_PAYABLE(còn) / CREDIT DEPOSIT_RECEIVABLE
  //                     (CHỈ phần mất là chi phí chung)
  //   ASSET_PURCHASE  : DEBIT PREPAID_ASSET / CREDIT CASH                      (0 chi phí lúc mua)
  //   DEPRECIATION    : DEBIT EXPENSE / CREDIT PREPAID_ASSET                   (chi phí sinh dần, Σ ≤ nguyên giá)
  // Producer: apps/api/src/modules/ledger/deposit.service.ts + shared-asset.service.ts.
  "DEPOSIT_PLACE",
  "DEPOSIT_REFUND",
  "DEPOSIT_FORFEIT",
  "ASSET_PURCHASE",
  "DEPRECIATION",
  // 🔴🔴 US-E20.F1-0207 (@UC9321–@UC9325) — HỤI/HỌ (ROSCA). Sổ hụi là POOL tổng-bằng-không:
  //   HUI_CONTRIB    : DEBIT CASH / CREDIT MEMBER_RECEIVABLE(người đóng)   ⇒ tiền VÀO pool
  //   HUI_COMMISSION : DEBIT MEMBER_RECEIVABLE(người hốt) / CREDIT MEMBER_PAYABLE(chủ hụi)
  //                    ⇒ hoa hồng = BÚT TOÁN RIÊNG, 0 chân CASH (không lẫn payout — P1.2)
  //   HUI_TAKE       : DEBIT MEMBER_RECEIVABLE(người hốt) / CREDIT CASH    ⇒ tiền RA pool (net)
  // 🔴 Σ VÀO = Σ RA mỗi kỳ: Σ đóng (gross) = net người hốt + hoa hồng. Σ MEMBER_RECEIVABLE toàn dây
  // = 0 (trò chơi tổng-bằng-không) — người ĐÃ hốt VẪN nợ pool cho tới khi đóng hết các kỳ sau.
  // Producer: apps/api/src/modules/hui/hui.service.ts.
  "HUI_CONTRIB",
  "HUI_TAKE",
  "HUI_COMMISSION",
  // 🔴🔴 US-E20.F2-0208 (@UC9327–@UC9330) — SỔ HIẾU HỈ (nghĩa vụ XÃ HỘI, KHÔNG phải nợ):
  //   GIFT_GIVEN    : DEBIT RECIPROCAL_RECEIVABLE(người đi lễ) / CREDIT RECIPROCAL_PAYABLE(người nhận)
  //   GIFT_RECEIVED : chiều ngược lại
  // 🔴 CẢ HAI: 0 CHÂN CASH ⇒ Σ CASH của sổ BẤT BIẾN khi cá nhân mừng cá nhân (tiền túi, không qua
  // quỹ). Khi QUỸ đứng ra mừng/nhận, dòng tiền THẬT đi ở BÚT TOÁN RIÊNG bằng txType SẴN CÓ (EXPENSE
  // / OTHER_INCOME) — KHÔNG trộn chân CASH vào GIFT_* (P1.2 không dòng lai).
  // 🔴 TRUNG TÍNH ở read-surface: GIFT_* KHÔNG phải thu/chi của quỹ ⇒ transactions.service liệt vào
  // NEUTRAL_TX_TYPES (nếu để rơi vào nhánh else sẽ hiện "CHI 500.000đ" trong khi quỹ không mất đồng
  // nào = NÓI DỐI người dùng — đúng lớp bug VALUE_FORM_SWITCH @UC9276).
  // Producer: apps/api/src/modules/reciprocal/reciprocal.service.ts.
  "GIFT_GIVEN",
  "GIFT_RECEIVED",
  // 🔴🔴 US-E20.F4-0210 (@UC9334–@UC9338) — ĐẦU TƯ NAV (ADR 0006). HAI txType, CẢ HAI ĐỀU LÀ
  // **ĐỔI HÌNH THÁI GIÁ TRỊ** (CASH ↔ ASSET_HOLDING), KHÔNG phải thu/chi:
  //   INVEST_BUY  : DEBIT ASSET_HOLDING(giá vốn) / CREDIT CASH(tiền mua)  ⇒ CASH −N, holding +N
  //   INVEST_SELL : DEBIT CASH(GIÁ VỐN phần bán) / CREDIT ASSET_HOLDING   ⇒ hoàn vốn, holding −giá vốn
  // 🔴 LÃI/LỖ ĐÃ THỰC HIỆN của lần bán đi ở **BÚT TOÁN RIÊNG** bằng txType SẴN CÓ (KHÔNG trộn vốn với
  // lãi — P1.2 không dòng lai): lãi ⇒ OTHER_INCOME (DEBIT CASH / CREDIT INCOME); lỗ ⇒ EXPENSE
  // (DEBIT EXPENSE / CREDIT CASH). ⇒ Σ CASH sau bán = giá vốn + lãi (hoặc − lỗ) = ĐÚNG tiền thu về.
  // 🔴 TRUNG TÍNH ở read-surface: INVEST_BUY/INVEST_SELL vào `NEUTRAL_TX_TYPES` (transactions.service)
  // — giá trị ròng của quỹ KHÔNG đổi khi đổi tiền lấy tài sản; để rơi vào nhánh else sẽ hiện
  // "CHI 10.000.000đ" trong khi quỹ không nghèo đi đồng nào = NÓI DỐI (đúng lớp bug VALUE_FORM_SWITCH
  // @UC9276 / GIFT_* @UC9330). Lãi/lỗ THẬT vẫn hiện đầy đủ qua OTHER_INCOME/EXPENSE.
  // 🔴 KHÔNG có txType nào cho "định giá lại": giá lên/xuống ⇒ 0 bút toán (bất biến số một).
  // Producer: apps/api/src/modules/invest/invest.service.ts.
  "INVEST_BUY",
  "INVEST_SELL",
  // 🔴 FUND-DEBT-REPAY-PATH (QĐ PO 06/10/2026 Q6 — PO DUYỆT migration CHECK) — PHÂN LOẠI LẠI nợ-với-quỹ của phần ứng trước đã cấn rồi bị
  // thu hồi (`advance_credit_clawback`): lúc chuyển nợ thành KHOẢN PHẢI ĐÓNG, dư Nợ U nằm trên `MEMBER_PAYABLE(m)` (ứng trước bị đảo sau khi đã
  // cấn) dời sang `MEMBER_RECEIVABLE(m)` — tài khoản mà đường thu (CONTRIBUTION: DEBIT CASH / CREDIT MEMBER_RECEIVABLE) sẽ tất toán:
  //   FUND_DEBT_RECLASS : DEBIT MEMBER_RECEIVABLE(m) U / CREDIT MEMBER_PAYABLE(m) U  ⇒ Σ CASH bất biến, ròng theo người bất biến.
  // KHÔNG tái dùng được txType cũ: `SETTLEMENT` cùng HÌNH (D RECEIVABLE / C PAYABLE) nhưng là nghĩa vụ PEER (hai người, `creditorMemberId`),
  // gắn vào nó là đọc nhầm thành "A trả B"; `OFFSET`/`ADVANCE_CREDIT` có chân/nghĩa khác. TRUNG TÍNH (`NEUTRAL_TX_TYPES`) — không thu, không
  // chi; KHÔNG trong danh sách sửa nhầm (`LEDGER_CORRECTABLE_TX_TYPES`). Producer: money-engine `modules/debts/fund-debt-due.producer.ts`.
  "FUND_DEBT_RECLASS",
] as const;

export type TxType = (typeof TX_TYPES)[number];

/** Nguồn sự thật của `side` — DB có CHECK `ledger_lines_side_check`. */
export const SIDES = ["DEBIT", "CREDIT"] as const;

export type Side = (typeof SIDES)[number];

export interface LedgerLineDraft {
  readonly accountId: string;
  readonly side: Side;
  readonly amountMinor: bigint; // > 0
  readonly currency: string;
}

export interface LedgerTransactionDraft {
  readonly fundId: string;
  readonly txType: TxType;
  readonly lines: readonly LedgerLineDraft[];
  readonly idempotencyKey?: string;
}

/**
 * Bất biến double-entry: Σ(DEBIT) = Σ(CREDIT), mỗi line amount > 0, ≥ 2 lines.
 * Trả về true nếu cân bằng; ném lỗi nếu vi phạm (để service từ chối trước khi chạm DB).
 */
export function assertBalanced(tx: LedgerTransactionDraft): true {
  if (tx.lines.length < 2) {
    throw new Error(`[ledger] transaction cần ≥ 2 lines (có ${tx.lines.length})`);
  }
  let debit = 0n;
  let credit = 0n;
  for (const line of tx.lines) {
    if (line.amountMinor <= 0n) {
      throw new Error(`[ledger] amountMinor phải > 0 (account ${line.accountId})`);
    }
    if (line.side === "DEBIT") debit += line.amountMinor;
    else credit += line.amountMinor;
  }
  if (debit !== credit) {
    throw new Error(`[ledger] mất cân bằng: debit=${debit} credit=${credit}`);
  }
  return true;
}

/**
 * Đảo toàn bộ transaction gốc thành REVERSAL: mỗi line đổi DEBIT↔CREDIT, amount giữ nguyên.
 * Gốc bất biến (append-only); reversal là transaction MỚI (TDD §7.4).
 */
export function buildReversal(original: LedgerTransactionDraft): LedgerTransactionDraft {
  return {
    fundId: original.fundId,
    txType: "REVERSAL",
    lines: original.lines.map((l) => ({
      ...l,
      side: l.side === "DEBIT" ? "CREDIT" : "DEBIT",
    })),
  };
}

/**
 * 🔴 PHÂN LOẠI THU/CHI Ở PHẠM VI **BÁO CÁO NHÓM** — nguồn sự thật DUY NHẤT (US-AI-0302).
 *
 * 🔴🔴 ĐỌC KỸ TRƯỚC KHI "DRY CHO GỌN": repo có **HAI** khái niệm thu/chi, cố ý khác nhau.
 *
 *  (1) **BÁO CÁO NHÓM** (bảng dưới đây) — dùng cho tổng thu/chi kỳ, biểu đồ, CSV, và câu trả lời
 *      của trợ lý. CỐ Ý **KHÔNG** gồm `FUND_TRANSFER_IN/OUT` và `VALUE_FORM_SWITCH`: chuyển giữa hai
 *      sổ của CÙNG nhóm, hay đổi hình thái tiền mặt↔bank, **không phải tiền mới vào** cũng **không
 *      phải tiền rời nhóm**. Cộng vào là **thổi phồng báo cáo** (số ảo, double-count).
 *
 *  (2) **XEM MỘT SỔ** (`transactions.service.ts`) — mũi tên thu/chi THEO SỔ ĐANG XEM, nên
 *      `FUND_TRANSFER_IN` LÀ "thu" của sổ đó. Đúng cho view một sổ, SAI cho báo cáo nhóm.
 *
 * ⇒ Gộp hai tập này làm một là tạo ra bug tiền. Nếu cần dùng, hãy chọn ĐÚNG phạm vi.
 *
 * VÌ SAO ĐƯA VỀ ĐÂY: audit 22/07 đo được **3 bản chép tay** của tập báo cáo
 * (`fund-dashboard.service.ts`, `home-summary.service.ts`, `report-csv.ts`) và trợ lý AI thì dùng
 * một tập **THỨ TƯ, THIẾU 4 LOẠI** (chỉ `CONTRIBUTION`) ⇒ hỏi dashboard và hỏi trợ lý ra **hai con số
 * tổng thu khác nhau cho cùng một tháng**. Không ai sai cú pháp; drift lặng lẽ giữa các bản chép.
 */
export const REPORT_INCOME_TX_TYPES: ReadonlySet<string> = new Set([
  "CONTRIBUTION",
  "SPONSORSHIP_MEMBER",
  "SPONSORSHIP_EXTERNAL",
  "OTHER_INCOME",
  "LOAN_REPAYMENT",
]);

/** Chi ở phạm vi BÁO CÁO NHÓM. Xem docblock `REPORT_INCOME_TX_TYPES` về hai phạm vi. */
export const REPORT_EXPENSE_TX_TYPES: ReadonlySet<string> = new Set(["EXPENSE", "LOAN_OUT"]);

/**
 * 🔴 US-INFRA-0301 lớp 5 — PHẠM VI THỨ HAI: **XEM MỘT SỔ** (`transactions.service.ts`).
 *
 * Khác `REPORT_INCOME_TX_TYPES` đúng MỘT phần tử: `FUND_TRANSFER_IN`. Chuyển từ sổ khác sang **LÀ
 * tiền vào sổ đang xem** ⇒ mũi tên "thu" của sổ đó là ĐÚNG. Nhưng ở phạm vi BÁO CÁO NHÓM thì không —
 * tiền chỉ dời chỗ trong cùng nhóm, cộng vào là thổi phồng.
 *
 * ⚠️ **KHÔNG gộp hai tập này.** Một phần tử khác biệt đó chính là ranh giới giữa "đúng" và "số ảo".
 * Khai riêng ở đây, cạnh nhau, để người sửa nhìn thấy CẢ HAI và biết mình đang đổi phạm vi nào.
 *
 * 🔴 AI-READ-DIRECTION: danh sách giao dịch KHÔNG còn phân loại bằng tập này (nó thiếu tiền vào không phải "thu": nộp hộ, ứng trước…)
 * mà bằng bảng đầy đủ `LEDGER_VIEW_TX_DIRECTION` bên dưới; test ép tập này ⊆ phần `income` của bảng.
 */
export const LEDGER_VIEW_INCOME_TX_TYPES: ReadonlySet<string> = new Set([
  ...REPORT_INCOME_TX_TYPES,
  "FUND_TRANSFER_IN",
]);

/**
 * 🔴 PHẠM VI THỨ BA: txType **TRUNG TÍNH** — không phải thu, cũng không phải chi, ở MỌI phạm vi.
 *
 * `VALUE_FORM_SWITCH` (đổi hình thái tiền mặt↔bank trong cùng sổ) và các bút toán hiếu hỉ / đầu tư:
 * Σ CASH của sổ **bất biến** hoặc chuyển động không mang nghĩa thu-chi nhóm. Lọt vào danh sách thu
 * hay chi đều làm sai báo cáo.
 */
export const NEUTRAL_TX_TYPES: ReadonlySet<string> = new Set([
  "VALUE_FORM_SWITCH",
  "GIFT_GIVEN",
  "GIFT_RECEIVED",
  "INVEST_BUY",
  "INVEST_SELL",
  // FUND-DEBT-REPAY-PATH — phân loại lại nợ-với-quỹ giữa hai tài khoản của CÙNG một người: 0 tiền vào/ra quỹ.
  "FUND_DEBT_RECLASS",
]);

/**
 * 🔴 MỘT NGUỒN cho vị-từ "bút toán HIỂN THỊ cho người dùng" (DEVELOP-RED-USER-SEARCH · FIX-1272b L-7 · P5.3): mọi bề mặt end-user đọc
 * `ledger_transactions` (danh sách giao dịch, bảng điều khiển, tìm kiếm theo CHỮ lẫn theo ID…) loại `NEUTRAL_TX_TYPES` qua hàm này — bút toán
 * trung tính không phải giao dịch của quỹ, và `FUND_DEBT_RECLASS` lộ việc huỷ ghi nhận + nợ với quỹ của một người. Ẩn với MỌI vai.
 * Trả đối tượng MỚI mỗi lần (người gọi trải vào `where` của Prisma).
 */
export interface VisibleTxWhere {
  readonly txType: { notIn: string[] };
  /** PUBLIC-STATEMENT-AND-REVERSAL — REVERSAL của bút toán TRUNG TÍNH cũng ẩn (nó chỉ có nghĩa qua bản gốc ẩn; hiện ra là lộ "có bút toán
   *  nội bộ bị đảo" + số tiền). Khoá quan hệ `reverses` (không `NOT`/`AND`/`OR`) để trải vào `where` của người gọi không đè khoá có sẵn.
   *  `isNot` trên quan hệ tuỳ chọn GIỮ dòng không có bản gốc (`reversesTxId` null) — đo ở int-spec. */
  readonly reverses: { isNot: { txType: { in: string[] } } };
}

export function visibleTxTypeWhere(): VisibleTxWhere {
  return { txType: { notIn: [...NEUTRAL_TX_TYPES] }, reverses: { isNot: { txType: { in: [...NEUTRAL_TX_TYPES] } } } };
}

/**
 * Cùng vị-từ với `visibleTxTypeWhere`, cho chỗ ĐÃ tải bản ghi (đường ghi cần tải bản gốc dù ẩn, rồi mới trả lời y hệt id không tồn tại).
 * `reversesTxType` BẮT BUỘC khai (txType bản gốc khi đây là REVERSAL, `null` khi không phải) — người gọi phải select `reverses.txType`.
 */
export function isUserVisibleTx(tx: { readonly txType: string; readonly reversesTxType: string | null }): boolean {
  return !NEUTRAL_TX_TYPES.has(tx.txType) && (tx.reversesTxType === null || !NEUTRAL_TX_TYPES.has(tx.reversesTxType));
}

/**
 * 🔴 AI-READ-DIRECTION (@UC0064 @UC0205) — CHIỀU TIỀN của từng txType ở phạm vi **XEM MỘT SỔ** (danh sách giao dịch
 * `transactions.service.ts` — màn Lịch sử giao dịch + trợ lý `READ-FUND-MONEY` "giao dịch gần đây" / "danh sách chi").
 *
 * Trước bản vá danh sách đó chỉ có MỘT tập (`LEDGER_VIEW_INCOME_TX_TYPES`) và coi MỌI txType ngoài tập là "chi" ⇒ `PAY_ON_BEHALF` /
 * `ADVANCE_CREDIT` (tiền VÀO quỹ — D CASH) và `OFFSET` / `REFUND` / `SETTLEMENT` (0 chân tiền mặt) bị kể là khoản chi: "tháng này chi
 * những khoản nào?" ⇒ "300.000 đ (Nộp hộ)". Bảng đóng dưới đây là nguồn DUY NHẤT của chiều thu/chi ở phạm vi này; `Record` theo `TxType`
 * ⇒ thêm txType vào whitelist mà quên khai chiều thì **typecheck đỏ**, và `ledger-view-direction.test.ts` đối chiếu bảng với `TX_TYPES`
 * (tự khám phá) cùng một bảng kỳ vọng chép tay — không còn nhánh "else = chi" nào để rơi vào lặng lẽ.
 *
 * LUẬT (đọc từ chân sổ của producer):
 *  · `income`  — có chân DEBIT CASH: tiền thật vào sổ đang xem (kể cả tiền vào không phải "thu báo cáo": nộp hộ, ứng trước, chuyển sổ đến).
 *  · `expense` — có chân CREDIT CASH (tiền ra) hoặc DEBIT EXPENSE (chi phí của quỹ — khoản chi người ứng, khấu hao, phần cọc mất).
 *  · `neutral` — không chạm tiền mặt lẫn chi phí (người↔người, cấn trừ, phân loại lại, đổi hình thái) + mọi `NEUTRAL_TX_TYPES`.
 * ⚠️ Cọc (`DEPOSIT_PLACE`/`DEPOSIT_REFUND`) đi theo nguồn QUỸ (C CASH / D CASH); cọc do thành viên ứng có chân MEMBER_PAYABLE thay CASH —
 * txType không phân biệt được hai nguồn, nên phiên bản này giữ chiều của nguồn quỹ (chưa có màn nào cho cọc — E20 để phase sau).
 *
 * KHÔNG thay ba phạm vi kia: tổng thu/chi báo cáo vẫn là `REPORT_*_TX_TYPES` (nộp hộ vắng ở đó là trạng-thái-sẵn-có, xem
 * `FEED_MONEY_IN_TX_TYPES`), ẩn/hiện vẫn là `visibleTxTypeWhere`. `ledger-view-direction.test.ts` ép: thu báo cáo + `LEDGER_VIEW_INCOME_TX_TYPES`
 * ⊆ `income`, `REPORT_EXPENSE_TX_TYPES` ⊆ `expense`, `NEUTRAL_TX_TYPES` ⊆ `neutral`.
 */
export type LedgerViewDirection = "income" | "expense" | "neutral";

export const LEDGER_VIEW_TX_DIRECTION: Readonly<Record<Exclude<TxType, "REVERSAL">, LedgerViewDirection>> = Object.freeze({
  CONTRIBUTION: "income",
  EXPENSE: "expense",
  SPONSORSHIP_MEMBER: "income",
  SPONSORSHIP_EXTERNAL: "income",
  OTHER_INCOME: "income",
  LOAN_OUT: "expense",
  LOAN_REPAYMENT: "income",
  SETTLEMENT: "neutral",
  PAY_ON_BEHALF: "income",
  OFFSET: "neutral",
  ADVANCE_CREDIT: "income",
  REFUND: "neutral",
  FUND_TRANSFER_OUT: "expense",
  FUND_TRANSFER_IN: "income",
  VALUE_FORM_SWITCH: "neutral",
  DEPOSIT_PLACE: "expense",
  DEPOSIT_REFUND: "income",
  DEPOSIT_FORFEIT: "expense",
  ASSET_PURCHASE: "expense",
  DEPRECIATION: "expense",
  HUI_CONTRIB: "income",
  HUI_TAKE: "expense",
  HUI_COMMISSION: "neutral",
  GIFT_GIVEN: "neutral",
  GIFT_RECEIVED: "neutral",
  INVEST_BUY: "neutral",
  INVEST_SELL: "neutral",
  FUND_DEBT_RECLASS: "neutral",
});

function directionOfOwnType(txType: string): LedgerViewDirection {
  // `Object.hasOwn` — tra thẳng bảng object literal thì "constructor"/"toString" trả một hàm (cùng lý lẽ `READ_FUND_MONEY_INDICATORS`).
  return Object.hasOwn(LEDGER_VIEW_TX_DIRECTION, txType)
    ? LEDGER_VIEW_TX_DIRECTION[txType as keyof typeof LEDGER_VIEW_TX_DIRECTION]
    : "neutral";
}

/**
 * Chiều hiển thị của MỘT bút toán. `REVERSAL` đi theo chiều bản GỐC (`reversesTxType` — txType bản gốc, `null` khi không phải REVERSAL hoặc
 * không rõ gốc): cặp "đã đảo" + "khoản ghi ngược" của một khoản chi nằm cùng danh sách chi. REVERSAL không rõ gốc, hoặc txType ngoài
 * whitelist ⇒ `neutral` — không đoán thu hay chi.
 */
export function ledgerViewDirectionOf(txType: string, reversesTxType: string | null): LedgerViewDirection {
  if (txType !== "REVERSAL") return directionOfOwnType(txType);
  return reversesTxType === null || reversesTxType === "REVERSAL" ? "neutral" : directionOfOwnType(reversesTxType);
}

/** txType (không gồm REVERSAL) có chiều `direction` — cho bộ lọc truy vấn. Mảng MỚI mỗi lần (người gọi trải vào `where`). */
export function ledgerViewTxTypesOf(direction: LedgerViewDirection): string[] {
  return Object.entries(LEDGER_VIEW_TX_DIRECTION)
    .filter(([, d]) => d === direction)
    .map(([t]) => t);
}

/**
 * 🔴 PHẠM VI THỨ TƯ: **FEED HOẠT ĐỘNG NHÓM — "ai đó ĐÃ NỘP QUỸ"** (US-E24.F12-0361 @UC9591).
 * Dùng bởi `apps/api/src/modules/engagement/activity-feed.service.ts`.
 *
 * 🔴🔴 VÌ SAO KHÔNG DÙNG LẠI `REPORT_INCOME_TX_TYPES` — đây là tập **HẸP HƠN THẬT SỰ**, không phải
 * bản chép thiếu. Feed trả lời câu *"X đã nộp quỹ"*, nên chỉ nhận txType vừa (a) có chân **DEBIT
 * CASH** (tiền thật vào quỹ) vừa (b) gắn `memberDueId` (nghĩa vụ của MỘT người được hoàn thành):
 *  - `CONTRIBUTION`  — 3 đường xác nhận: two_party (`approve-decline.service.ts`), admin_one_party
 *                      (`record-paid.service.ts`), gateway_auto (`gateway-webhook.service.ts`)
 *  - `PAY_ON_BEHALF` — đường thứ 4 (`pay-on-behalf.service.ts`): người khác nộp thay, tiền VẪN vào
 *                      quỹ. Bỏ sót loại này = bỏ sót đúng một đường xác nhận.
 *
 * 🔴 Tập này **KHÔNG phải tập con** của `REPORT_INCOME_TX_TYPES` — hai chiều đều khác, đo thật:
 *  - `REPORT_INCOME \ FEED` = **4**: `SPONSORSHIP_MEMBER`, `SPONSORSHIP_EXTERNAL`, `OTHER_INCOME`,
 *    `LOAN_REPAYMENT`. Đều là tiền vào quỹ THẬT (nên ĐÚNG ở phạm vi báo cáo) nhưng **không ai
 *    "hoàn thành nghĩa vụ đợt thu"** cả ⇒ dựng thành dòng *"X đã nộp quỹ"* là **nói sai chuyện đã
 *    xảy ra**. Cộng vào báo cáo là đúng; cộng vào feed là bịa một sự kiện không có.
 *  - `FEED \ REPORT_INCOME` = **1**: `PAY_ON_BEHALF`. Nó có mặt ở feed vì tiền THẬT SỰ vào quỹ và
 *    một nghĩa vụ THẬT SỰ được hoàn thành. Việc nó vắng ở tập báo cáo là trạng-thái-sẵn-có của repo,
 *    **không** phải thứ task này thay đổi — đừng "sửa cho đối xứng" mà không đọc lại phạm vi báo cáo.
 *
 * Khác `LEDGER_VIEW_INCOME_TX_TYPES` thêm `FUND_TRANSFER_IN`: chuyển giữa hai sổ của CÙNG nhóm là
 * "thu" khi xem riêng sổ đích, nhưng **không ai nộp gì cả** ⇒ **KHÔNG BAO GIỜ** vào feed.
 *
 * `REVERSAL` KHÔNG có ở đây ⇒ bút toán đảo không tự đẻ item; bút toán gốc ĐÃ BỊ ĐẢO bị loại bằng
 * `reversedBy: { none: {} }` ở tầng query (hiện "đã nộp 200k" cho khoản đã undo-record = nói dối
 * về tiền).
 *
 * ⚠️ Khai ở đây — cạnh 3 phạm vi kia — để người thêm `txType` mới nhìn thấy **cả bốn** và biết mình
 * đang đổi phạm vi nào. Guard `concept-drift.invariants.spec.ts` cấm chép tập này ra nơi khác (bắt mảng/`Set` ≥2 phần tử;
 * KHÔNG đọc literal trong chuỗi SQL).
 */
export const FEED_MONEY_IN_TX_TYPES: readonly string[] = ["CONTRIBUTION", "PAY_ON_BEHALF"];

/**
 * 🔴 PHẠM VI THỨ NĂM: **FEED HOẠT ĐỘNG — "ai đó ĐÓNG GÓP THÊM, ngoài nghĩa vụ"**
 * (US-E7.F1-0724 · @UC0074 · QĐ người dùng 07/09/2026).
 *
 * Tách khỏi [FEED_MONEY_IN_TX_TYPES] ngay trên chứ KHÔNG nhập vào — và đây là điểm dễ làm sai nhất
 * của cả task. Tập kia trả lời *"X đã NỘP QUỸ"*, tức hoàn thành một nghĩa vụ của đợt thu, nên mỗi
 * dòng của nó có `memberDueId` để lần ra người có nghĩa vụ. Khoản đóng góp thêm **không có nghĩa vụ
 * nào**: nhét `SPONSORSHIP_MEMBER` vào tập kia sẽ dựng câu "đã nộp quỹ" cho một việc khác hẳn, và
 * `resolvePayerNames` (đi qua `memberDueId`) trả `null` ⇒ dòng mất luôn tên người.
 *
 * ⇒ Hai tập, hai loại sự kiện, hai câu chữ. Cùng là `kind = income` khi lọc chip "Thu", vì tiền
 * THẬT SỰ vào quỹ ở cả hai.
 *
 * 🔴 CHỈ `SPONSORSHIP_MEMBER`, cố ý (QĐ người dùng 07/09/2026). Hai loại thu ngoài-nghĩa-vụ còn lại
 * ĐỨNG NGOÀI vì không có ai trong nhóm để nêu tên: `SPONSORSHIP_EXTERNAL` là người NGOÀI nhóm, và
 * `OTHER_INCOME` gồm lãi/hoàn phí — không có chủ thể người nào cả. Muốn mở thêm thì phải trả lời
 * trước câu "dòng đó nói tên ai", chứ không phải thêm chuỗi vào mảng này.
 *
 * ⚠️ Khai ở đây — cạnh 4 phạm vi kia — để người thêm `txType` mới nhìn thấy **cả năm**. Guard canh bản chép là
 * `sponsorship-member-literal.invariants.spec.ts` (US-E23.F1-0872): literal `SPONSORSHIP_MEMBER` (chuỗi · template · regex, kể cả
 * SQL) trong source của `apps/api` · `apps/worker` · `packages/money-engine` · `packages/domain` phải khớp allowlist ĐẾM theo từng
 * file ⇒ lệch là đỏ. Nó KHÔNG quét `packages/contracts` · `db` · `apps/admin` · `apps/mobile`. `concept-drift.invariants.spec.ts`
 * KHÔNG canh tập này — nó bỏ qua mảng < 2 phần tử và không đọc SQL.
 *
 * 🔴 US-E7.F1-0741 (08/09/2026) — TẬP NÀY NAY CÓ **HAI** NƠI ĐỌC, và tên `FEED_` chỉ nói nơi thứ
 * nhất. Nơi thứ hai là `sumMemberSponsorships` (money-engine `modules/groups/member-sponsorships.ts` từ US-E23.F1-0869 — app
 * lẫn dòng "<Tên> · Góp quỹ chung" của PDF thu chi gọi nó): cột *"đã đóng"* của một thành viên cộng đúng các txType này. Hai nơi PHẢI dùng chung một tập — thêm một loại thu vào feed
 * mà quên phép cộng (hoặc ngược lại) thì dòng hoạt động và con số tổng sẽ nói hai chuyện khác nhau
 * về cùng một khoản tiền, và người dùng nhìn thấy cả hai trên hai màn cạnh nhau.
 */
export const FEED_OTHER_INCOME_TX_TYPES: readonly string[] = ["SPONSORSHIP_MEMBER"];

/**
 * 🔴 US-E7.F1-0914 (@UC9591 · review PR #1287 H2) — loại bút toán được SỬA GHI CHÚ (`ledger_transaction_labels.note`).
 *
 * Danh sách ĐÓNG, ghép từ ĐÚNG hai tập feed mà màn "Chi tiết hoạt động" mở ô ghi chú: "Nộp quỹ" (`payment.confirmed` —
 * [FEED_MONEY_IN_TX_TYPES]) và "Đóng góp thêm" (`income.recorded` — [FEED_OTHER_INCOME_TX_TYPES]). Ghép chứ không chép: thêm một
 * loại vào feed (màn mở thêm) thì ghi chú theo ngay, và không có bản chép thứ hai để lệch. Ngoài tập ⇒ server 422
 * `LEDGER_TX_NOTE_NOT_EDITABLE` (khoản chi sửa ghi chú qua form khoản chi; REVERSAL/chuyển quỹ/… không có màn sửa ghi chú).
 * Mobile đọc cờ `noteEditable` server tính từ tập này (`GET …/label`) — không tự suy.
 */
export const LEDGER_TX_NOTE_EDITABLE_TX_TYPES: ReadonlySet<string> = new Set([
  ...FEED_MONEY_IN_TX_TYPES,
  ...FEED_OTHER_INCOME_TX_TYPES,
]);

/**
 * 🔴 PHẠM VI THỨ SÁU: **LỊCH SỬ NỘP CỦA MỘT SUẤT ở màn admin "Người tham gia" của đợt thu** (US-AE4.F7-1004, #1109).
 * Bút toán gắn `memberDueId` được kể là "đã ghi nhận nộp" (`CONTRIBUTION`) hoặc "đã đảo" (`REVERSAL`).
 *
 * Chuyển từ literal trong `admin-collection-participants.service.ts` về đây (guard @AF0.1 `concept-drift.invariants.spec.ts`)
 * — GIỮ NGUYÊN hành vi của #1109. ⚠️ Câu hỏi mở cho chủ #1109: `OFFSET` (cấn trừ, `offset.service.ts`) cũng ghi
 * `memberDueId` nhưng KHÔNG nằm trong tập này ⇒ suất được cấn trừ không hiện dòng lịch sử ở màn admin.
 */
export const ADMIN_DUE_HISTORY_TX_TYPES: readonly string[] = ["CONTRIBUTION", "REVERSAL"];
